import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { AppLocale, isAppLocale } from '@/domain/enums/appLocale';
import { translations } from '../locale';
import type { l10n } from '../locale/l10n';

interface LocaleContextType {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  l10n: l10n;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
};

interface LocaleProviderProps {
  children: ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  // Get initial locale from localStorage or default to English.
  // The persisted value is untrusted (stale, hand-edited, or written by a future
  // app version), so it is tested for membership rather than cast: a non-member
  // would make the `translations[locale]` lookup below yield `undefined` for
  // every `useLocale().l10n` consumer. English is the presentation-owned
  // fallback policy; the enum only answers whether the value is a member.
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const saved = localStorage.getItem('locale');
    return isAppLocale(saved) ? saved : AppLocale.EN;
  });

  // Get the translation object for the current locale
  const t = translations[locale];

  // Save locale to localStorage whenever it changes
  const setLocale = (newLocale: AppLocale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale, l10n: t }}>
      {children}
    </LocaleContext.Provider>
  );
};
